import axios, { AxiosResponse } from 'axios';
import { supabase } from './supabase';
import { otvoriFajl, ucitajNacin } from './otvaranje';

// Prazna vrijednost = isti origin (backend servira i frontend, npr. preko tunela).
// Zato ?? a ne || — inače bi prazan string pao na localhost.
const BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

export const api = axios.create({ baseURL: BASE });

// Automatski dodaj Bearer token iz Supabase sesije
api.interceptors.request.use(async (config) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }
  return config;
});

// Istekao token (npr. mobitel ili tab koji je dugo spavao, pa se timer za
// obnovu nije izvršio) backend odbija sa 401. Bez ovoga liste ostanu prazne
// a korisnik ne zna zašto. Jednom se pokuša obnoviti sesija i ponoviti zahtjev;
// ako ni to ne uspije, odjava vraća na prijavu (AuthContext sluša promjenu).
api.interceptors.response.use(undefined, async (error) => {
  const config = error.config;
  if (error.response?.status !== 401 || !config || config._ponovljen) {
    throw error;
  }
  config._ponovljen = true;
  const { data, error: refreshErr } = await supabase.auth.refreshSession();
  if (refreshErr || !data.session) {
    await supabase.auth.signOut();
    throw error;
  }
  config.headers.Authorization = `Bearer ${data.session.access_token}`;
  return api(config);
});

// ----------------------------------------------------------------
// Tipovi
// ----------------------------------------------------------------

export interface ObracunRecord {
  id: string;
  period: string;
  status: 'processing' | 'completed' | 'error';
  error_msg: string | null;
  created_at: string;
  // Samo u odgovoru /status: automatski preračun pokrenut pri preuzimanju
  preracun?: 'radi' | 'greska' | null;
  preracun_poruka?: string | null;
}

export interface SavjetnikResult {
  id: string;
  sifra_savjetnika: string;
  ime_savjetnika: string;
  bbs_tekuca: number | null;
  ukupno_bbs: number | null;
  neto_plata: number | null;
  ipl_ukupni: number | null;
  ipl_6mj: number | null;
  ipl_zadnji_mj: number | null;
  broj_novih_polica: number | null;
  npp_obrocna: number | null;
  created_at: string;
  storage_path_word: string | null;   // BBS mjesečna lista; prazno = nije napravljena
}

// ----------------------------------------------------------------
// API pozivi
// ----------------------------------------------------------------

// BBS faktor se ovdje ne šalje — uzima se iz opštih postavki, da se ista
// stopa ne bi zadavala na dva mjesta.
export async function uploadObracun(
  file1: File, file2: File, file3: File, period: string
): Promise<{ obracun_id: string; status: string }> {
  const form = new FormData();
  form.append('file1', file1);
  form.append('file2', file2);
  form.append('file3', file3);
  form.append('period', period);
  const { data } = await api.post('/api/obracun/upload', form);
  return data;
}

export async function getObracuni(): Promise<ObracunRecord[]> {
  const { data } = await api.get('/api/obracun/');
  return data;
}

export async function getObracunStatus(obracunId: string): Promise<ObracunRecord> {
  const { data } = await api.get(`/api/obracun/${obracunId}/status`);
  return data;
}

export async function otkljucajPeriod(period: string) {
  const { data } = await api.post(`/api/obracun/zakljucanost/${period}/otkljucaj`);
  return data;
}

export async function zakljucajPeriod(period: string) {
  const { data } = await api.post(`/api/obracun/zakljucanost/${period}/zakljucaj`);
  return data;
}

export async function getSavjetnici(obracunId: string, samoSvoje = false): Promise<SavjetnikResult[]> {
  const { data } = await api.get(`/api/obracun/${obracunId}/savjetnici`,
    samoSvoje ? { params: { samo_svoje: true } } : undefined);
  return data;
}

// ----------------------------------------------------------------
// Preuzimanje uz automatski preračun
//
// Backend prije svakog preuzimanja provjeri jesu li se podaci (dani rada,
// postavke…) promijenili otkad je aktuelni obračun napravljen. Ako jesu,
// vrati 202 i preračuna ga u pozadini; ovdje se tada čeka pa pita ponovo,
// da se nikad ne preuzme fajl bez zadnjih unosa.
// ----------------------------------------------------------------

const pauza = (ms: number) => new Promise(r => setTimeout(r, ms));

async function svjeze<T>(
  obracunId: string,
  zahtjev: () => Promise<AxiosResponse<T>>,
  naCekanje?: () => void,
): Promise<T> {
  for (;;) {
    const res = await zahtjev();
    if (res.status !== 202) return res.data;
    naCekanje?.();
    for (;;) {
      await pauza(3000);
      const st = await getObracunStatus(obracunId);
      if (st.preracun === 'greska') {
        throw new Error(st.preracun_poruka || 'Preračun obračuna nije uspio.');
      }
      if (st.preracun !== 'radi') break;
    }
  }
}

/** Čitljiva poruka iz greške preuzimanja (i kad je odgovor blob). */
export async function porukaGreske(e: unknown): Promise<string> {
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try { return JSON.parse(await data.text()).detail; } catch { }
  }
  const detail = (data as { detail?: unknown })?.detail;
  if (typeof detail === 'string') return detail;
  return e instanceof Error ? e.message : 'Preuzimanje nije uspjelo.';
}

export async function getDownloadUrl(
  obracunId: string, savjetnikId: string, tip: 'opp' | 'bbs' | 'word', naCekanje?: () => void,
  samoSvoje = false,
): Promise<string> {
  const data = await svjeze<{ url: string }>(obracunId,
    () => api.get(`/api/obracun/${obracunId}/download/${savjetnikId}/${tip}`,
      samoSvoje ? { params: { samo_svoje: true } } : undefined), naCekanje);
  return data.url;
}

export async function updateIpl(
  obracunId: string, sifra: string,
  ipl_ukupni: number, ipl_6mj: number, ipl_zadnji_mj: number
): Promise<{ ipl_ukupni: number; ipl_6mj: number; ipl_zadnji_mj: number; fakturisana_npp: number }> {
  const form = new FormData();
  form.append('ipl_ukupni', String(ipl_ukupni));
  form.append('ipl_6mj', String(ipl_6mj));
  form.append('ipl_zadnji_mj', String(ipl_zadnji_mj));
  const { data } = await api.patch(`/api/obracun/${obracunId}/savjetnik/${encodeURIComponent(sifra)}/ipl`, form);
  return data;
}

// Obje vraćaju funkciju koja otvara fajl, a ne otvaraju ga same: poslije
// čekanja na preračun preglednik više ne smatra otvaranje korisnikovim
// klikom i tiho ga blokira, pa korisnik mora kliknuti još jednom.

export async function downloadObracunFinal(
  obracunId: string, period: string, naCekanje?: () => void,
): Promise<() => void> {
  const data = await svjeze<{ url: string }>(obracunId,
    () => api.get(`/api/obracun/${obracunId}/download-obracun`), naCekanje);
  // Poštuje isti izbor "otvori u Excelu / preuzmi" kao i fajlovi savjetnika.
  return () => otvoriFajl(data.url, ucitajNacin(), `OBRACUN_${period}.xlsx`);
}

export async function downloadPlata(
  obracunId: string, period: string, naCekanje?: () => void,
): Promise<() => void> {
  const data = await svjeze<{ url: string }>(obracunId,
    () => api.get(`/api/plata/${obracunId}/preuzmi`), naCekanje);
  return () => otvoriFajl(data.url, ucitajNacin(), `Plata_${period}.xlsx`);
}

/** Šalje list plata na zadane adrese — samo direktor. */
export async function posaljiPlatu(
  obracunId: string, primaoci: string[], poruka: string, naCekanje?: () => void,
): Promise<{ primaoci: string[] }> {
  return svjeze(obracunId,
    () => api.post(`/api/plata/${obracunId}/posalji`, { primaoci, poruka }), naCekanje);
}

export async function downloadZip(
  obracunId: string, period: string, naCekanje?: () => void,
): Promise<() => void> {
  const data = await svjeze<Blob>(obracunId,
    () => api.get(`/api/obracun/${obracunId}/download-zip`, { responseType: 'blob' }),
    naCekanje);
  return () => {
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `obracun_${period}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  };
}

// ----------------------------------------------------------------
// Slanje izvještaja savjetnicima na mail
// ----------------------------------------------------------------

export interface MailSlanje {
  status: 'poslano' | 'greska';
  email: string;
  greska: string | null;
  created_at: string;
}

export interface MailSavjetnik {
  id: string;
  sifra_savjetnika: string;
  ime_savjetnika: string;
  email: string;
  /** Prazno = može mu se poslati; inače razlog zašto ne može. */
  prepreka: string;
  zadnje_slanje: MailSlanje | null;
}

export interface MailPregled {
  period: string;
  podesen: boolean;
  poruka: string;
  savjetnici: MailSavjetnik[];
}

export interface MailStanje {
  stanje: 'radi' | 'gotovo' | 'greska' | null;
  poruka?: string;
  ukupno?: number;
  uradjeno?: number;
  poslano?: number;
  neuspjelo?: number;
}

export async function getMailPregled(obracunId: string): Promise<MailPregled> {
  const { data } = await api.get(`/api/mail/${obracunId}`);
  return data;
}

export async function getMailStanje(obracunId: string): Promise<MailStanje> {
  const { data } = await api.get(`/api/mail/${obracunId}/stanje`);
  return data;
}

/**
 * Pokreće slanje i čeka da tura završi.
 *
 * Backend prvo, kao i preuzimanje, provjeri jesu li se podaci promijenili i
 * po potrebi preračuna obračun — tada odgovori `{preracunava:true}` i ovdje
 * se čeka pa traži ponovo, da savjetniku nikad ne ode zastarjeli fajl.
 */
export async function posaljiIzvjestaje(
  obracunId: string,
  opcije: { sifre?: string[]; ponovo?: boolean } = {},
  naCekanje?: () => void,
): Promise<MailStanje & { preskoceni?: { ime_savjetnika: string; razlog: string }[] }> {
  for (;;) {
    const { data } = await api.post(`/api/mail/${obracunId}`, {
      sifre:  opcije.sifre ?? null,
      ponovo: opcije.ponovo ?? false,
    });

    if (data.preracunava) {
      naCekanje?.();
      for (;;) {
        await pauza(3000);
        const st = await getObracunStatus(obracunId);
        if (st.preracun === 'greska') {
          throw new Error(st.preracun_poruka || 'Preračun obračuna nije uspio.');
        }
        if (st.preracun !== 'radi') break;
      }
      continue;
    }

    if (!data.pokrenuto) return { stanje: 'gotovo', ukupno: 0, ...data };

    for (;;) {
      await pauza(2000);
      const st = await getMailStanje(obracunId);
      if (st.stanje !== 'radi') return { ...st, preskoceni: data.preskoceni };
    }
  }
}
